import { useState } from 'react';
import { QueueState, EventType } from '@/types/director.types';
import { CameraPreset } from '@/types/camera.types';
import { ShotData } from '@/types/shot.types';

interface DebugOverlayProps {
  queueState: QueueState;
  displayedShotIds: string[];
  onReset?: () => Promise<void>;
}

/**
 * Debug overlay showing event queue state and system status
 * Useful for development and testing
 */
export function DebugOverlay({ queueState, displayedShotIds, onReset }: DebugOverlayProps) {
  const [isExpanded, setIsExpanded] = useState(false);
  const [resetStatus, setResetStatus] = useState<'idle' | 'resetting' | 'success' | 'error'>('idle');

  const formatEventPayload = (event: QueueState['currentEvent']) => {
    if (!event) return '';

    if (event.type === EventType.CAMERA_MOVE) {
      const preset = event.payload as CameraPreset;
      return `pos(${preset.position.x.toFixed(0)}, ${preset.position.y.toFixed(0)}, ${preset.position.z.toFixed(0)})`;
    } else {
      const shot = event.payload as ShotData;
      return `${shot.id} - ${shot.playerInfo.name}`;
    }
  };

  const handleReset = async () => {
    if (!onReset) return;

    setResetStatus('resetting');
    try {
      await onReset(); // Calls App's coordinated reset (backend + frontend + canvas)
      setResetStatus('success');
      setTimeout(() => setResetStatus('idle'), 2000);
    } catch (error) {
      console.error('Failed to reset:', error);
      setResetStatus('error');
      setTimeout(() => setResetStatus('idle'), 2000);
    }
  };

  const getResetButtonContent = () => {
    switch (resetStatus) {
      case 'resetting':
        return '⏳ Resetting...';
      case 'success':
        return '✓ Reset!';
      case 'error':
        return '✗ Failed';
      default:
        return '🔄 Reset';
    }
  };

  const getResetButtonColor = () => {
    switch (resetStatus) {
      case 'resetting':
        return 'rgba(255, 165, 0, 0.3)';
      case 'success':
        return 'rgba(76, 175, 80, 0.3)';
      case 'error':
        return 'rgba(244, 67, 54, 0.3)';
      default:
        return 'rgba(255, 255, 255, 0.1)';
    }
  };

  return (
    <div
      style={{
        position: 'fixed',
        top: '10px',
        right: '10px',
        backgroundColor: 'rgba(0, 0, 0, 0.85)',
        color: '#fff',
        padding: '12px',
        borderRadius: '8px',
        fontFamily: 'monospace',
        fontSize: '12px',
        maxWidth: '400px',
        zIndex: 1000,
        border: '1px solid rgba(255, 255, 255, 0.2)',
      }}
    >
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: isExpanded ? '10px' : '0',
        }}
      >
        <h3 style={{ margin: 0, marginRight: '6px', fontSize: '14px', fontWeight: 'bold' }}>
          🎬 Director Debug
        </h3>
        <div style={{ display: 'flex', gap: '6px' }}>
          <button
            onClick={handleReset}
            disabled={resetStatus === 'resetting'}
            style={{
              background: getResetButtonColor(),
              border: '1px solid rgba(255, 255, 255, 0.3)',
              color: '#fff',
              padding: '4px 8px',
              borderRadius: '4px',
              cursor: resetStatus === 'resetting' ? 'not-allowed' : 'pointer',
              fontSize: '10px',
              opacity: resetStatus === 'resetting' ? 0.6 : 1,
            }}
          >
            {getResetButtonContent()}
          </button>
          <button
            onClick={() => setIsExpanded(!isExpanded)}
            style={{
              background: 'rgba(255, 255, 255, 0.1)',
              border: '1px solid rgba(255, 255, 255, 0.3)',
              color: '#fff',
              padding: '4px 8px',
              borderRadius: '4px',
              cursor: 'pointer',
              fontSize: '10px',
            }}
          >
            {isExpanded ? 'Collapse' : 'Expand'}
          </button>
        </div>
      </div>

      {isExpanded && (
        <div>
          {/* Status */}
          <div style={{ marginBottom: '10px' }}>
            <div
              style={{
                display: 'inline-block',
                padding: '2px 6px',
                borderRadius: '4px',
                backgroundColor: queueState.isBusy
                  ? 'rgba(255, 165, 0, 0.3)'
                  : 'rgba(0, 255, 0, 0.3)',
                fontSize: '10px',
                fontWeight: 'bold',
              }}
            >
              {queueState.isBusy ? '⚙️ BUSY' : '✅ IDLE'}
            </div>
          </div>

          {/* Current Event */}
          <div style={{ marginBottom: '10px' }}>
            <div style={{ fontWeight: 'bold', marginBottom: '4px', opacity: 0.7 }}>
              Current Event:
            </div>
            <div
              style={{
                backgroundColor: 'rgba(255, 255, 255, 0.1)',
                padding: '6px',
                borderRadius: '4px',
                minHeight: '30px',
              }}
            >
              {queueState.currentEvent ? (
                <div>
                  <div style={{ color: '#4CAF50', fontWeight: 'bold' }}>
                    {queueState.currentEvent.type}
                  </div>
                  <div style={{ fontSize: '10px', opacity: 0.8 }}>
                    {formatEventPayload(queueState.currentEvent)}
                  </div>
                </div>
              ) : (
                <div style={{ opacity: 0.5 }}>None</div>
              )}
            </div>
          </div>

          {/* Queue */}
          <div style={{ marginBottom: '10px' }}>
            <div style={{ fontWeight: 'bold', marginBottom: '4px', opacity: 0.7 }}>
              Queued Events ({queueState.queue.length}):
            </div>
            <div
              style={{
                backgroundColor: 'rgba(255, 255, 255, 0.1)',
                padding: '6px',
                borderRadius: '4px',
                maxHeight: '150px',
                overflowY: 'auto',
              }}
            >
              {queueState.queue.length > 0 ? (
                queueState.queue.map((event, index) => (
                  <div
                    key={event.id}
                    style={{
                      marginBottom: '4px',
                      paddingBottom: '4px',
                      borderBottom:
                        index < queueState.queue.length - 1
                          ? '1px solid rgba(255, 255, 255, 0.1)'
                          : 'none',
                    }}
                  >
                    <div style={{ fontSize: '10px' }}>
                      <span style={{ color: '#2196F3', fontWeight: 'bold' }}>
                        {event.type}
                      </span>
                      <span style={{ opacity: 0.7, marginLeft: '6px' }}>
                        {formatEventPayload(event)}
                      </span>
                    </div>
                  </div>
                ))
              ) : (
                <div style={{ opacity: 0.5, fontSize: '10px' }}>Empty</div>
              )}
            </div>
          </div>

          {/* Displayed Shots */}
          <div>
            <div style={{ fontWeight: 'bold', marginBottom: '4px', opacity: 0.7 }}>
              Displayed Shots ({displayedShotIds.length}):
            </div>
            <div
              style={{
                backgroundColor: 'rgba(255, 255, 255, 0.1)',
                padding: '6px',
                borderRadius: '4px',
                fontSize: '10px',
              }}
            >
              {displayedShotIds.length > 0 ? (
                displayedShotIds.join(', ')
              ) : (
                <span style={{ opacity: 0.5 }}>None</span>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
